import React, { useState, useEffect } from 'react';
import { Send, Mail, Users, School, GraduationCap, AlertCircle, CheckCircle, X } from 'lucide-react';
import { auth } from '../../firebase/config';
import { buildApiUrl, API_CONFIG } from '../../firebase/config';

const AdminEmailMessaging = () => {
  const [formData, setFormData] = useState({
    targetType: 'school_roster',
    targetId: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [options, setOptions] = useState({
    schools: [],
    gradeBands: [],
    coaches: []
  });
  const [loadingOptions, setLoadingOptions] = useState(true);

  const TARGET_TYPE_OPTIONS = [
    { id: 'school_roster', label: 'Specific School Roster', icon: School },
    { id: 'grade_band', label: 'Specific Grade Band', icon: GraduationCap },
    { id: 'coach_roster', label: "Specific Coach's Assigned Roster", icon: Users },
    { id: 'all_members', label: 'All School Pickup Page Members', icon: Mail }
  ];

  useEffect(() => {
    loadMessagingOptions();
  }, []);

  // Debug effect to monitor options changes
  useEffect(() => {
    console.log('Options state updated:', options);
    console.log('Coaches in options:', options.coaches);
    console.log('Coaches length:', options.coaches?.length);
  }, [options]);

  const loadMessagingOptions = async () => {
    try {
      setLoadingOptions(true);
      const idToken = await auth.currentUser.getIdToken(true);
      const url = buildApiUrl(API_CONFIG.endpoints.messaging.admin.options);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      const result = await response.json();
      console.log('Messaging options API response:', result);
      console.log('Response status:', response.status);
      
      if (result.success) {
        console.log('Options data:', result.data);
        console.log('Coaches array:', result.data.coaches);
        console.log('Coaches length:', result.data.coaches?.length);
        setOptions(result.data);
      } else {
        console.error('API returned error:', result.error);
        if (result.error && result.error.includes('Access denied')) {
          setError('Admin access denied. Please ensure you are logged in as an admin and the authentication middleware is deployed.');
        } else {
          throw new Error(result.error || 'Failed to load options');
        }
      }
    } catch (error) {
      console.error('Error loading messaging options:', error);
      setError('Failed to load messaging options. Please refresh the page.');
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Validate required fields
      if (!formData.message.trim()) {
        setError('Please enter a message.');
        return;
      }

      if (!formData.subject.trim()) {
        setError('Please enter a subject.');
        return;
      }

      if (formData.targetType !== 'all_members' && !formData.targetId) {
        setError('Please select a target for this message.');
        return;
      }

      // Get Firebase ID token
      const idToken = await auth.currentUser.getIdToken(true);

      // Send email using messaging API
      const url = buildApiUrl(API_CONFIG.endpoints.messaging.admin.send);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          targetType: formData.targetType,
          targetId: formData.targetId,
          subject: formData.subject,
          message: formData.message
        })
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to send email');
      }

      setSuccess(`Email sent successfully to ${result.data.recipientCount} recipients!`);
      
      // Reset form
      setFormData({
        targetType: 'school_roster',
        targetId: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending email:', error);
      setError(error.message || 'Failed to send email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getTargetLabel = () => {
    switch (formData.targetType) {
      case 'school_roster':
        return 'Select School Roster';
      case 'grade_band':
        return 'Select Grade Band';
      case 'coach_roster':
        return 'Select Coach';
      case 'all_members':
        return 'All Members';
      default:
        return 'Select Target';
    }
  };

  const getTargetOptions = () => {
    switch (formData.targetType) {
      case 'school_roster':
        return options.schools.map(school => ({
          id: school.id,
          label: school.name || school.schoolName || school.id
        }));
      case 'grade_band':
        return options.gradeBands.map(grade => ({
          id: grade,
          label: grade
        }));
      case 'coach_roster':
        return options.coaches.map(coach => ({
          id: coach.id,
          label: `${coach.firstName} ${coach.lastName}`.trim() || coach.email || coach.id
        }));
      default:
        return [];
    }
  };

  const getEmptyStateMessage = () => {
    switch (formData.targetType) {
      case 'school_roster':
        return 'No school rosters found. Please add rosters to the system first.';
      case 'grade_band':
        return 'No grade bands found. Please ensure rosters have grade information assigned.';
      case 'coach_roster':
        return 'No coaches found. Please add coaches to the system first.';
      default:
        return 'No options available for this target type.';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Send Email to Pickup Rosters</h2>
          <p className="text-gray-600 mt-1">Send emails to school pickup roster members</p>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={() => setError('')}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
          <p className="text-green-600 text-sm">{success}</p>
          <button
            onClick={() => setSuccess('')}
            className="ml-auto text-green-400 hover:text-green-600"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Email Form */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Target Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <Mail size={16} className="inline mr-1" />
              Target Audience
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {TARGET_TYPE_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      handleInputChange('targetType', option.id);
                      handleInputChange('targetId', '');
                    }}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      formData.targetType === option.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Icon size={20} className={formData.targetType === option.id ? 'text-blue-600' : 'text-gray-600'} />
                      <span className={`text-sm font-medium ${formData.targetType === option.id ? 'text-blue-800' : 'text-gray-800'}`}>
                        {option.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Target Selection (if not all_members) */}
          {formData.targetType !== 'all_members' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {getTargetLabel()}
              </label>
              {loadingOptions ? (
                <div className="w-full p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm">
                  Loading options...
                </div>
              ) : getTargetOptions().length === 0 ? (
                <div className="w-full p-3 border border-gray-200 rounded-lg bg-yellow-50 text-yellow-700 text-sm">
                  {getEmptyStateMessage()}
                </div>
              ) : (
                <select
                  value={formData.targetId}
                  onChange={(e) => handleInputChange('targetId', e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Choose a target</option>
                  {getTargetOptions().map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder="Enter email subject..."
              className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message *
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder="Type your message to the recipients..."
              rows="8"
              className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-500">
                Keep messages clear and concise for better engagement
              </span>
              <span className="text-xs text-gray-500">
                {formData.message.length} characters
              </span>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Mail className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Email Information</p>
                <ul className="list-disc list-inside space-y-1 text-blue-600">
                  <li>Emails will be sent to parent email addresses in the selected rosters</li>
                  <li>A copy will be CC'd to the admin email</li>
                  <li>Replies will go to the admin email</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
              disabled={loading || loadingOptions}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Send Email
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminEmailMessaging;
